/**
 * How a request failed, in the two kinds that matter. Cut from KangaruRide's
 * `mobile/src/api/errors.ts` (its ADR-0023): the difference between "the
 * server said no" and "we do not know what happened" is what every retry and
 * every offline queue in this app is built on.
 *
 * Screens branch on `code`, never on the message: the words are written for
 * people and will be reworded. The codes InGo's API sends today include
 * VALIDATION_FAILED, INVALID_CREDENTIALS, UNAUTHENTICATED, FORBIDDEN,
 * PASSWORD_CHANGE_REQUIRED, TEMPORARY_PASSWORD_EXPIRED, NOT_FOUND, THROTTLED,
 * ROUTE_UNAVAILABLE, TARIFF_NOT_CONFIGURED and SERVER_ERROR.
 */

/** The server answered, and the answer was a refusal: it arrived and did not take effect. */
export class ApiError extends Error {
  constructor({ code, message, status, errors = {}, retryAfterSeconds = null, serverMessage = null, envelope = false, requestId = null, meta = {} }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    /** Field messages from a 422, keyed by input name: `{ email: ['…'] }`. */
    this.errors = errors;
    /** From `Retry-After` on a 429 or 503, in seconds; null when the header was absent or unreadable. */
    this.retryAfterSeconds = retryAfterSeconds;
    /** The server's own sentence, or null when the payload carried none. */
    this.serverMessage = serverMessage;
    /** Whether the payload was InGo's envelope (a string `code`), not a proxy's page. */
    this.envelope = envelope;
    /** `X-Request-Id`, for a support conversation; null when the header was not readable. */
    this.requestId = requestId;
    this.meta = meta;
  }
}

/**
 * The request may or may not have been applied: a dropped connection, a DNS
 * failure, a timeout. Never retried blind when it was a write.
 */
export class NetworkError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isApiError(error) {
  return error instanceof ApiError;
}

/** A 5xx counts: a gateway's 502 says nothing about whether the server behind it acted. */
export function isOutcomeUnknown(error) {
  return error instanceof NetworkError || (isApiError(error) && error.status >= 500);
}

/** Whether trying again could give a different answer. A 401 is not: nothing changes until the person signs in. */
export function isRetryable(error) {
  return error instanceof NetworkError || (isApiError(error) && (error.status >= 500 || error.status === 429));
}

/**
 * The sentence for a person when a request failed. The server's words when it
 * answered (a field's message before the envelope's), the caller's `offline`
 * sentence when nothing arrived, and a plain one otherwise.
 */
export function refusalMessage(error, offline = 'Cannot reach InGo. Check the connection and try again.') {
  if (isApiError(error)) {
    if (error.code === 'THROTTLED') {
      return error.retryAfterSeconds
        ? `Too many tries. Wait ${error.retryAfterSeconds} seconds and try again.`
        : 'Too many tries. Wait a minute and try again.';
    }
    // A password carried over from the old app lapses 30 days after the
    // import (InGo, 4 Oct 2026). The kit says to ask whoever gave it, and here
    // nobody did: the reset link on the same page is the way.
    if (error.code === 'TEMPORARY_PASSWORD_EXPIRED') {
      return 'Your old password has expired. Use Forgot password? to choose a new one.';
    }

    const field = Object.values(error.errors).find((messages) => Array.isArray(messages) && messages.length > 0);

    return field ? field[0] : error.message;
  }

  if (error instanceof NetworkError) {
    return offline;
  }

  return 'Something went wrong on this device. Try again.';
}
